import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService, CurrentUser } from '../../auth.service';
import { ContactAutocompleteResult } from '../../services/contacts/contact-autocomplete.service';
import {
  CreateCurrentQuoteRequest,
  CurrentQuoteSummary,
  CustomerFreightPresentation,
  CustomerQuoteCondition,
  CustomerRateLineItem,
  CustomerRateMoney,
  CustomerRateProduct,
  CustomerRateQuoteApiService,
  CustomerRateQuoteRequest,
  CustomerRateQuoteResponse,
  CustomerRoutePreviewResponse,
  CustomerRateQuoteStatus,
  CustomerRateZoneMatch,
  CustomerShippingTarget,
  CustomerShippingTargetAddress,
  LocationCandidate,
  RateFreight,
  RateFreightItem,
  RateLocation,
  ZoneResolution
} from './customer-rate-quote-api.service';

type PreferredRateMethod = 1 | 2 | 3 | 4 | null;
type ThemeMode = 'dark' | 'light';
type StatusTone = 'idle' | 'success' | 'warning' | 'danger' | 'info';
type FreightFieldKey = keyof CustomerFreightPresentation['field_visibility'];

interface RateLocationForm {
  postal_code: string;
  city: string;
  province: string;
  country: string;
}

interface RateFreightForm {
  total_weight_lb: number | null;
  linear_feet: number | null;
  handling_units: number | null;
  cubic_feet: number | null;
  density_lb_per_cuft: number | null;
  delivery_type: number | null;
  preferred_rate_method: PreferredRateMethod;
  load_type: string;
}

interface RateQuoteForm {
  external_reference_number: string;
  origin: RateLocationForm;
  destination: RateLocationForm;
  quote_date: string;
  freight: RateFreightForm;
}

interface RateMethodOption {
  value: PreferredRateMethod;
  label: string;
  icon: string;
}

interface CountryOption {
  value: string;
  label: string;
}

interface LocationCandidateView {
  side: 'Origin' | 'Destination';
  candidate: LocationCandidate;
}

interface ShippingOriginOption {
  id: string;
  label: string;
  subline: string;
  location: RateLocation;
  target: CustomerShippingTarget;
  address: CustomerShippingTargetAddress | null;
}

interface SelectedCatalogPackage {
  product: CustomerRateProduct;
  package_quantity: number;
}

interface ManualPackageDraft {
  label: string;
  package_quantity: number;
  length: number | null;
  width: number | null;
  height: number | null;
  weight: number | null;
  measurement_unit: 'IN' | 'CM';
  weight_unit: 'LB' | 'KG';
  stackable: boolean;
  floor_rotation_allowed: boolean;
}

interface SelectedManualPackage extends ManualPackageDraft {
  id: number;
  length: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-customer-rate-quote',
  templateUrl: './customer-rate-quote.component.html',
  styleUrls: ['./customer-rate-quote.component.scss']
})
export class CustomerRateQuoteComponent implements OnInit {
  readonly rateMethodOptions: RateMethodOption[] = [
    { value: null, label: 'Best Available', icon: 'fa-wand-magic-sparkles' },
    { value: 1, label: 'Weight', icon: 'fa-weight-hanging' },
    { value: 2, label: 'Linear Feet', icon: 'fa-ruler-horizontal' },
    { value: 3, label: 'Cubic', icon: 'fa-cube' },
    { value: 4, label: 'Density', icon: 'fa-boxes-stacked' }
  ];

  readonly countryOptions: CountryOption[] = [
    { value: 'CA', label: 'Canada' },
    { value: 'US', label: 'United States' }
  ];

  quoteForm: RateQuoteForm = this.createDefaultForm();
  themeMode: ThemeMode = 'dark';
  customerControl = new FormControl<ContactAutocompleteResult | string | null>(null);
  selectedCustomer: ContactAutocompleteResult | null = null;
  currentUser: CurrentUser | null = null;
  canSelectCustomer = false;
  response: CustomerRateQuoteResponse | null = null;
  createdQuote: CurrentQuoteSummary | null = null;
  quoteConditionsAccepted = false;
  quoteCreating = false;
  quotePdfDownloading = false;
  quotePdfError = '';
  quoteCreateError = '';
  routePreview: CustomerRoutePreviewResponse | null = null;
  routeMapUrl: SafeUrl | null = null;
  routePreviewLoading = false;
  routePreviewError = '';
  errorMessage = '';
  loading = false;
  shippingTargetsLoading = false;
  shippingTargetsLoaded = false;
  shippingTargetsError = '';
  shippingTargets: CustomerShippingTarget[] = [];
  selectedShippingOriginId = '';
  selectedShippingOrigin: ShippingOriginOption | null = null;
  selectedOriginZoneUuid = '';
  selectedDestinationZoneUuid = '';
  customerProducts: CustomerRateProduct[] = [];
  productMatches: CustomerRateProduct[] = [];
  selectedProducts: SelectedCatalogPackage[] = [];
  manualPackages: SelectedManualPackage[] = [];
  manualPackageForm: ManualPackageDraft = this.createManualPackageForm();
  productSelectionMode: 'full_catalog' | 'autocomplete' | null = null;
  productSearch = '';
  productTotal = 0;
  productsLoading = false;
  productsLoaded = false;
  productsError = '';
  productMoreMatches = false;
  freightPresentation: CustomerFreightPresentation | null = null;

  private readonly themeStorageKey = 'customer-rate-quote-theme';
  private productSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private manualPackageSequence = 0;
  private shippingTargetsRequestSequence = 0;
  private productsRequestSequence = 0;
  private lastRatedRequest: CustomerRateQuoteRequest | null = null;
  private ratedInputFingerprint = '';
  private quoteIdempotencyKey = '';

  constructor(
    private readonly rateQuoteApi: CustomerRateQuoteApiService,
    private readonly toastr: ToastrService,
    private readonly authService: AuthService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.initializeTheme();

    this.authService.ensureCurrentUserLoaded().subscribe({
      next: (currentUser) => {
        this.currentUser = currentUser;
        this.canSelectCustomer = this.isAdminUser(currentUser);

        if (!this.canSelectCustomer) {
          this.clearSelectedCustomer();
          this.loadShippingTargets();
          this.loadCustomerProducts();
        } else {
          this.clearShippingTargets();
          this.clearCustomerProducts();
        }
      },
      error: () => {
        this.currentUser = null;
        this.canSelectCustomer = false;
        this.clearSelectedCustomer();
      }
    });

    this.customerControl.valueChanges.subscribe((value) => {
      if (!value || typeof value === 'string') {
        const hadSelectedCustomer = this.selectedCustomer !== null;
        this.selectedCustomer = null;
        if (hadSelectedCustomer && this.canSelectCustomer) {
          this.clearShippingTargets();
          this.clearSelectedShippingOrigin(false);
          this.clearOriginForm();
          this.clearRoutePreview();
          this.clearSelectedFreightItems();
          this.clearCustomerProducts();
          this.resetFreightForCustomer();
        }
      }
    });
  }

  get customerScopeLabel(): string {
    if (this.canSelectCustomer) {
      return this.selectedCustomer?.label || 'Select Customer';
    }

    return this.response ? this.customerName : 'Authenticated Customer';
  }

  get customerName(): string {
    const customer = this.response?.customer;
    return customer?.display_name || customer?.contact_organization || customer?.legal_name || 'Customer';
  }

  get hasConfiguredFreightPresentation(): boolean {
    return this.freightPresentation?.configured === true;
  }

  get freightPresentationName(): string {
    return this.freightPresentation?.profile_name || 'Customer freight profile';
  }

  get freightPresentationSummary(): string {
    return this.freightPresentation?.summary || '';
  }

  get showPackageBuilder(): boolean {
    if (this.productsLoading && !this.freightPresentation) {
      return false;
    }
    return this.freightPresentation?.show_package_builder ?? true;
  }

  get allowCatalogProducts(): boolean {
    return this.freightPresentation?.allow_catalog_products ?? true;
  }

  get allowManualPackages(): boolean {
    return this.freightPresentation?.allow_manual_packages ?? true;
  }

  get showManualFreight(): boolean {
    if (this.productsLoading && !this.freightPresentation) {
      return false;
    }
    return this.freightPresentation?.show_manual_metrics ?? true;
  }

  get showRateMethodPicker(): boolean {
    return this.freightPresentation?.show_rate_method_picker ?? true;
  }

  get usesCalculatedLinearFeet(): boolean {
    return this.freightPresentation?.mode === 'catalog_equipment_linear_feet'
      && this.totalSelectedPackageCount > 0;
  }

  isFreightFieldVisible(field: FreightFieldKey): boolean {
    return this.freightPresentation?.field_visibility[field] ?? true;
  }

  get selectedRateMethod(): string {
    return this.response?.selected_rate?.rate_method_label || 'n/a';
  }

  get linearFeetUsed(): string {
    const responseValue = this.firstFreightNumber([
      'linear_feet_used',
      'billable_linear_feet',
      'rated_linear_feet',
      'rate_linear_feet',
      'chargeable_linear_feet',
      'linear_feet',
      'linear_ft',
      'linearFeet'
    ]);
    const fallbackValue = this.numberFromUnknown(this.quoteForm.freight.linear_feet);
    const value = responseValue ?? fallbackValue;

    return value === undefined ? 'n/a' : this.formatLinearFeet(value);
  }

  get shippingOriginOptions(): ShippingOriginOption[] {
    return this.shippingTargets.flatMap((target, targetIndex) => this.shippingOriginOptionsForTarget(target, targetIndex));
  }

  get showShippingOrigins(): boolean {
    return this.shippingTargetsLoading || this.shippingTargetsLoaded || this.shippingOriginOptions.length > 0 || Boolean(this.shippingTargetsError);
  }

  get originZoneName(): string {
    return this.response?.selected_rate?.origin_zone?.zone_name
      || this.response?.lane.origin_zone?.zone_name
      || this.response?.selected_rate?.origin_zone?.name
      || this.response?.lane.origin_zone?.name
      || 'n/a';
  }

  get destinationZoneName(): string {
    return this.response?.selected_rate?.destination_zone?.zone_name
      || this.response?.lane.destination_zone?.zone_name
      || this.response?.selected_rate?.destination_zone?.name
      || this.response?.lane.destination_zone?.name
      || 'n/a';
  }

  get transitTime(): string {
    const transitTime = this.response?.selected_rate?.transit_time;
    if (transitTime === null || transitTime === undefined || transitTime === '') {
      return 'n/a';
    }

    return String(transitTime);
  }

  get selectedEquipmentName(): string {
    const equipment = this.response?.freight?.['selected_equipment'] as { code?: string; name?: string } | undefined;
    if (!equipment) {
      return 'n/a';
    }

    return [equipment.code, equipment.name].filter(Boolean).join(' / ') || 'n/a';
  }

  get billableMiles(): string {
    const value = this.numberFromUnknown(this.response?.freight?.['rated_miles']);
    return value === undefined ? 'n/a' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value) + ' mi';
  }

  get displayedProductMatches(): CustomerRateProduct[] {
    if (this.productSelectionMode === 'autocomplete') {
      return this.productMatches;
    }

    const search = this.productSearch.trim().toLowerCase();
    const products = search
      ? this.customerProducts.filter((product) => this.productSearchText(product).includes(search))
      : this.customerProducts;
    return products.slice(0, 100);
  }

  get totalSelectedPackageCount(): number {
    return this.selectedProducts.reduce((total, selected) => total + Number(selected.package_quantity || 0), 0)
      + this.manualPackages.reduce((total, selected) => total + Number(selected.package_quantity || 0), 0);
  }

  get quoteTotal(): CustomerRateMoney | null {
    return this.response?.quote.total ?? null;
  }

  get quoteCurrency(): string {
    return this.validCurrency(this.quoteTotal?.currency)
      || this.validCurrency(this.response?.quote.currency)
      || this.currencyForDestinationCountry()
      || 'n/a';
  }

  get lineItems(): CustomerRateLineItem[] {
    return this.response?.quote.line_items ?? [];
  }

  get quoteNotes(): string[] {
    return this.response?.quote.notes ?? [];
  }

  get quoteConditions(): CustomerQuoteCondition[] {
    return this.response?.quote.conditions ?? [];
  }

  get quoteInputsUnchanged(): boolean {
    return this.lastRatedRequest !== null
      && this.ratedInputFingerprint !== ''
      && this.ratedInputFingerprint === this.quoteRequestFingerprint(this.buildPayload());
  }

  get canCreateCurrentQuote(): boolean {
    return this.response?.status === 'rated'
      && this.lastRatedRequest !== null
      && this.quoteInputsUnchanged
      && this.quoteConditions.length > 0
      && this.quoteConditionsAccepted
      && this.quoteTotal !== null
      && Number.isInteger(this.quoteTotal.amount_cents)
      && !this.quoteCreating
      && this.createdQuote === null;
  }

  get warnings(): string[] {
    return this.response?.warnings ?? [];
  }

  get originZoneMatches(): CustomerRateZoneMatch[] {
    return this.response?.lane.origin_zone?.matches ?? [];
  }

  get destinationZoneMatches(): CustomerRateZoneMatch[] {
    return this.response?.lane.destination_zone?.matches ?? [];
  }

  get showZoneSelection(): boolean {
    return this.response?.status === 'needs_zone_selection';
  }

  get canRetryWithZones(): boolean {
    if (!this.showZoneSelection) {
      return false;
    }

    return Boolean(this.currentOriginZoneUuid() && this.currentDestinationZoneUuid());
  }

  get locationCandidates(): LocationCandidateView[] {
    const originCandidates = this.response?.lane.origin.candidates ?? [];
    const destinationCandidates = this.response?.lane.destination.candidates ?? [];

    return [
      ...originCandidates.map((candidate) => ({ side: 'Origin' as const, candidate })),
      ...destinationCandidates.map((candidate) => ({ side: 'Destination' as const, candidate }))
    ];
  }

  get statusTone(): StatusTone {
    if (!this.response) {
      return 'idle';
    }

    return this.toneForStatus(this.response.status);
  }

  quoteRate(): void {
    this.submitQuote(false);
  }

  retryWithZoneSelection(): void {
    if (!this.canRetryWithZones) {
      this.toastr.warning('Select an origin and destination zone.');
      return;
    }

    this.submitQuote(true);
  }

  createQuoteFromRate(): void {
    if (this.response?.status !== 'rated' || !this.lastRatedRequest || !this.quoteTotal) {
      this.toastr.warning('Check the rate before creating a quote.');
      return;
    }

    if (!this.quoteInputsUnchanged) {
      this.quoteConditionsAccepted = false;
      this.quoteCreateError = 'The freight or lane changed after this rate was calculated. Check the rate again.';
      this.toastr.warning(this.quoteCreateError);
      return;
    }

    if (!this.quoteConditionsAccepted) {
      this.toastr.warning('Accept the quote conditions before creating the quote.');
      return;
    }

    const expectedCurrency = this.validCurrency(this.quoteTotal.currency)
      || this.validCurrency(this.response.quote.currency);
    if (!expectedCurrency || !Number.isInteger(this.quoteTotal.amount_cents)) {
      this.quoteCreateError = 'The rated total is missing a valid currency or amount.';
      this.toastr.error(this.quoteCreateError);
      return;
    }

    if (!this.quoteIdempotencyKey) {
      this.quoteIdempotencyKey = this.newIdempotencyKey();
    }

    const payload: CreateCurrentQuoteRequest = {
      source: 'customer_rate_quote',
      ...(this.effectiveSelectedCustomerUuid()
        ? { customer_contact_uuid: this.effectiveSelectedCustomerUuid() }
        : {}),
      rate_request: this.cloneQuoteRequest(this.lastRatedRequest),
      conditions_accepted: true,
      expected_total_amount_cents: this.quoteTotal.amount_cents,
      expected_currency: expectedCurrency
    };

    this.quoteCreating = true;
    this.quoteCreateError = '';
    this.rateQuoteApi.createCurrentQuote(payload, this.quoteIdempotencyKey).subscribe({
      next: (result) => {
        this.createdQuote = result.data;
        this.quoteCreating = false;
        this.toastr.success(`${result.data.display_quote_number} and PDF saved.`);
      },
      error: (error: HttpErrorResponse) => {
        this.quoteCreating = false;
        this.quoteCreateError = this.errorText(error, 'Unable to create the quote.');
        if (error.status === 409) {
          this.lastRatedRequest = null;
          this.ratedInputFingerprint = '';
          this.quoteConditionsAccepted = false;
          this.quoteCreateError += ' Check the rate again before retrying.';
        }
        this.toastr.error(this.quoteCreateError);
      }
    });
  }

  downloadQuotePdf(): void {
    if (!this.createdQuote || this.quotePdfDownloading) {
      return;
    }
    const savedQuote = this.createdQuote;
    this.quotePdfDownloading = true;
    this.quotePdfError = '';
    this.rateQuoteApi.downloadCurrentQuotePdf(savedQuote.quote_uuid).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = savedQuote.pdf?.filename || `${savedQuote.display_quote_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.quotePdfDownloading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.quotePdfDownloading = false;
        this.quotePdfError = 'Unable to download the saved quote PDF. Please retry.';
        this.toastr.error(this.quotePdfError);
      }
    });
  }

  setPreferredRateMethod(method: PreferredRateMethod): void {
    this.quoteForm.freight.preferred_rate_method = method;
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeLabel(): string {
    return this.themeMode === "dark" ? "Light" : "Dark";
  }

  getThemeIcon(): string {
    return this.themeMode === "dark" ? "fa-sun" : "fa-moon";
  }

  onCustomerSelected(customer: ContactAutocompleteResult): void {
    this.selectedCustomer = customer;
    this.response = null;
    this.clearRoutePreview();
    this.selectedOriginZoneUuid = '';
    this.selectedDestinationZoneUuid = '';
    this.clearSelectedShippingOrigin(false);
    this.clearOriginForm();
    this.resetFreightForCustomer();
    this.loadShippingTargets(customer.value);
    this.clearSelectedFreightItems();
    this.loadCustomerProducts(customer.value);
  }

  clearSelectedCustomer(): void {
    this.selectedCustomer = null;
    this.customerControl.setValue(null, { emitEvent: false });
    this.clearShippingTargets();
    this.clearSelectedShippingOrigin(false);
    this.clearOriginForm();
    this.clearRoutePreview();
    this.clearSelectedFreightItems();
    this.clearCustomerProducts();
    this.resetFreightForCustomer();
  }

  onProductSearchChanged(value: string): void {
    this.productSearch = value;
    if (this.productSelectionMode !== 'autocomplete') {
      return;
    }

    if (this.productSearchTimer) {
      clearTimeout(this.productSearchTimer);
    }
    const search = value.trim();
    if (search.length < 3) {
      this.productMatches = [];
      this.productMoreMatches = false;
      return;
    }

    this.productSearchTimer = setTimeout(() => {
      this.loadCustomerProducts(this.effectiveSelectedCustomerUuid(), search);
    }, 250);
  }

  addCatalogProduct(product: CustomerRateProduct): void {
    const existing = this.selectedProducts.find((selected) => selected.product.customer_product_uuid === product.customer_product_uuid);
    if (existing) {
      existing.package_quantity = Math.max(1, Number(existing.package_quantity || 0) + 1);
    } else {
      this.selectedProducts.push({ product, package_quantity: 1 });
    }
    this.productSearch = '';
    this.productMatches = [];
    this.response = null;
    this.clearRoutePreview();
  }

  removeCatalogProduct(index: number): void {
    this.selectedProducts.splice(index, 1);
    this.response = null;
  }

  addManualPackage(): void {
    const draft = this.manualPackageForm;
    const length = this.positiveNumber(draft.length);
    const width = this.positiveNumber(draft.width);
    const height = this.positiveNumber(draft.height);
    const quantity = this.positiveWholeNumber(draft.package_quantity);
    if (length === undefined || width === undefined || height === undefined || quantity === undefined) {
      this.toastr.warning('Custom packages require positive length, width, height, and whole package quantity.');
      return;
    }

    this.manualPackages.push({
      ...draft,
      id: ++this.manualPackageSequence,
      label: this.cleanString(draft.label) || `Custom package ${this.manualPackageSequence}`,
      package_quantity: quantity,
      length,
      width,
      height,
      weight: this.positiveNumber(draft.weight) ?? null
    });
    this.manualPackageForm = this.createManualPackageForm();
    this.response = null;
    this.clearRoutePreview();
  }

  removeManualPackage(index: number): void {
    this.manualPackages.splice(index, 1);
    this.response = null;
  }

  productDisplayName(product: CustomerRateProduct): string {
    return product.product_model_number || product.product_name || product.product_description || product.customer_product_uuid;
  }

  productSubline(product: CustomerRateProduct): string {
    const dimensions = [product.shipping_length, product.shipping_width, product.shipping_height]
      .every((value) => value !== null)
      ? `${product.shipping_length} × ${product.shipping_width} × ${product.shipping_height} ${product.measurement_unit_label}`
      : 'Dimensions unavailable';
    return [
      product.product_class ? `Class ${product.product_class}` : '',
      dimensions,
      product.floor_rotation_allowed ? 'Floor rotation allowed' : 'No floor rotation'
    ]
      .filter(Boolean)
      .join(' / ');
  }

  selectShippingOrigin(option: ShippingOriginOption): void {
    this.selectedShippingOriginId = option.id;
    this.selectedShippingOrigin = option;
    this.applyOriginLocation(option.location);
    this.response = null;
    this.clearRoutePreview();
    this.selectedOriginZoneUuid = '';
    this.selectedDestinationZoneUuid = '';
  }

  clearSelectedShippingOrigin(clearFields = false): void {
    this.selectedShippingOriginId = "";
    this.selectedShippingOrigin = null;

    if (clearFields) {
      this.clearOriginForm();
    }
  }

  onOriginManualEdit(): void {
    if (!this.selectedShippingOrigin) {
      return;
    }

    this.clearSelectedShippingOrigin(false);
    this.response = null;
    this.clearRoutePreview();
    this.selectedOriginZoneUuid = "";
    this.selectedDestinationZoneUuid = "";
  }

  resetQuote(): void {
    this.quoteForm = this.createDefaultForm();
    this.response = null;
    this.clearRoutePreview();
    this.errorMessage = '';
    this.selectedOriginZoneUuid = '';
    this.selectedDestinationZoneUuid = '';
    this.clearSelectedFreightItems();
    this.resetQuoteSubmissionState();

    if (this.canSelectCustomer) {
      this.clearSelectedCustomer();
    } else {
      this.clearSelectedShippingOrigin(false);
      this.applyFreightPresentation(this.freightPresentation);
    }
  }

  statusClass(status: CustomerRateQuoteStatus | null | undefined): string {
    return `rate-status--${this.toneForStatus(status)}`;
  }

  statusIcon(status: CustomerRateQuoteStatus | null | undefined): string {
    switch (status) {
      case 'rated':
        return 'fa-circle-check';
      case 'no_rate_found':
      case 'zone_not_found':
      case 'location_not_found':
        return 'fa-circle-exclamation';
      case 'needs_zone_selection':
        return 'fa-route';
      case 'needs_location_confirmation':
        return 'fa-location-dot';
      default:
        return 'fa-circle-info';
    }
  }

  statusTitle(status: CustomerRateQuoteStatus | null | undefined): string {
    switch (status) {
      case 'rated':
        return 'Rate Found';
      case 'needs_zone_selection':
        return 'Zone Selection Needed';
      case 'needs_location_confirmation':
        return 'Location Confirmation Needed';
      case 'location_not_found':
        return 'Location Not Found';
      case 'zone_not_found':
        return 'Zone Not Found';
      case 'no_rate_found':
        return 'No Rate Found';
      default:
        return 'Ready';
    }
  }

  statusMessage(status: CustomerRateQuoteStatus | null | undefined): string {
    switch (status) {
      case 'rated':
        return 'A customer rate matched this lane and freight.';
      case 'needs_zone_selection':
        return 'Multiple customer zones match this lane.';
      case 'needs_location_confirmation':
        return 'The location match needs more detail.';
      case 'location_not_found':
        return 'The origin or destination could not be resolved.';
      case 'zone_not_found':
        return 'The lane resolved, but one side is outside available customer zones.';
      case 'no_rate_found':
        return 'The lane resolved, but no active customer rate matched the freight band, date, or delivery type.';
      default:
        return 'No quote has been calculated yet.';
    }
  }

  zoneMatchLabel(match: CustomerRateZoneMatch): string {
    return match.zone_name || match.name || match.zone_id || match.zone_uuid;
  }

  zoneMatchMeta(match: CustomerRateZoneMatch): string {
    const parts = [
      match.zone_id ? `ID ${match.zone_id}` : '',
      match.country ?? '',
      this.formatConfidence(match.confidence)
    ].filter((part) => part !== '');

    return parts.length ? parts.join(' / ') : match.zone_uuid;
  }

  candidateLabel(candidate: LocationCandidate): string {
    return candidate.label
      || candidate.display_name
      || candidate.formatted_address
      || [candidate.city, candidate.province || candidate.state || candidate.region, candidate.postal_code || candidate.zip || candidate.postal, candidate.country]
        .filter((part) => part !== undefined && part !== null && String(part).trim() !== '')
        .join(', ')
      || 'Location candidate';
  }

  candidateMeta(candidate: LocationCandidate): string {
    const parts = [
      candidate.postal_code || candidate.zip || candidate.postal || '',
      candidate.country || '',
      this.formatConfidence(candidate.confidence)
    ].filter((part) => part !== '');

    return parts.length ? parts.join(' / ') : 'Candidate';
  }

  private currencyForDestinationCountry(): string {
    const country = this.normalizeCountryCode(
      this.response?.lane.destination.country
      || this.quoteForm.destination.country
    );

    if (country === 'US') {
      return 'USD';
    }

    if (country === 'CA') {
      return 'CAD';
    }

    return '';
  }

  private validCurrency(value: string | null | undefined): string {
    const currency = this.cleanString(value).toUpperCase();
    return /^[A-Z]{3}$/.test(currency) && currency !== 'N/A' ? currency : '';
  }

  private firstFreightNumber(keys: string[]): number | undefined {
    const freight = this.response?.freight ?? {};
    for (const key of keys) {
      const value = this.numberFromUnknown(freight[key]);
      if (value !== undefined) {
        return value;
      }
    }

    return undefined;
  }

  private numberFromUnknown(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private formatLinearFeet(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value) + ' ft';
  }

  formatMoney(total: CustomerRateMoney | null, currency = this.quoteCurrency): string {
    if (!total) {
      return 'n/a';
    }

    const formatCurrency = this.validCurrency(currency) || this.validCurrency(total.currency) || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formatCurrency
    }).format(total.amount);
  }

  lineItemLabel(item: CustomerRateLineItem): string {
    return item.label || item.name || item.description || 'Rate line';
  }

  lineItemAmount(item: CustomerRateLineItem): string {
    if (item.total) {
      return this.formatMoney(item.total);
    }

    if (typeof item.amount === 'number' && Number.isFinite(item.amount)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: item.currency || this.quoteTotal?.currency || 'USD'
      }).format(item.amount);
    }

    if (typeof item.amount_cents === 'number' && Number.isFinite(item.amount_cents)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: item.currency || this.quoteTotal?.currency || 'USD'
      }).format(item.amount_cents / 100);
    }

    return 'n/a';
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByZoneUuid(_index: number, match: CustomerRateZoneMatch): string {
    return match.zone_uuid;
  }

  private initializeTheme(): void {
    const storedTheme = localStorage.getItem(this.themeStorageKey);
    this.themeMode = storedTheme === 'light' ? 'light' : 'dark';
  }

  private isAdminUser(currentUser: CurrentUser | null): boolean {
    const roles = currentUser?.roles ?? [];
    return roles.some((role) => ['ADMIN', 'SUPER_ADMIN'].includes(role.toUpperCase()));
  }

  private loadShippingTargets(customerContactUuid?: string): void {
    const requestSequence = ++this.shippingTargetsRequestSequence;
    this.shippingTargetsLoading = true;
    this.shippingTargetsLoaded = false;
    this.shippingTargetsError = '';

    this.rateQuoteApi.listCustomerShippingTargets(customerContactUuid).subscribe({
      next: (response) => {
        if (requestSequence !== this.shippingTargetsRequestSequence) {
          return;
        }
        this.shippingTargets = response.data ?? [];
        this.shippingTargetsLoading = false;
        this.shippingTargetsLoaded = true;

        const options = this.shippingOriginOptions;
        if (options.length === 1 && !this.hasLocationValue(this.buildLocation(this.quoteForm.origin))) {
          this.selectShippingOrigin(options[0]);
        }
      },
      error: (error: HttpErrorResponse) => {
        if (requestSequence !== this.shippingTargetsRequestSequence) {
          return;
        }
        this.shippingTargets = [];
        this.shippingTargetsLoading = false;
        this.shippingTargetsLoaded = true;
        this.shippingTargetsError = this.errorText(error, 'Unable to load saved origins.');
      }
    });
  }

  private loadCustomerProducts(customerContactUuid?: string, search?: string): void {
    const requestSequence = ++this.productsRequestSequence;
    this.productsLoading = true;
    this.productsError = '';

    this.rateQuoteApi.listCustomerProducts(customerContactUuid, search).subscribe({
      next: (response) => {
        if (requestSequence !== this.productsRequestSequence) {
          return;
        }
        this.applyFreightPresentation(response.freight_presentation);
        this.productSelectionMode = response.selection_mode;
        this.productTotal = response.total_products ?? 0;
        this.productMoreMatches = response.more_matches ?? false;
        this.productsLoading = false;
        this.productsLoaded = true;

        if (response.selection_mode === 'full_catalog') {
          this.customerProducts = response.products ?? [];
          this.productMatches = [];
        } else {
          this.customerProducts = [];
          this.productMatches = response.products ?? [];
        }
      },
      error: (error: HttpErrorResponse) => {
        if (requestSequence !== this.productsRequestSequence) {
          return;
        }
        this.productsLoading = false;
        this.productsLoaded = true;
        this.productMatches = [];
        this.productsError = this.errorText(error, 'Unable to load customer products.');
      }
    });
  }

  private clearCustomerProducts(): void {
    this.productsRequestSequence += 1;
    this.customerProducts = [];
    this.productMatches = [];
    this.productSelectionMode = null;
    this.productTotal = 0;
    this.productSearch = '';
    this.productsLoading = false;
    this.productsLoaded = false;
    this.productsError = '';
    this.productMoreMatches = false;
    this.freightPresentation = null;
  }

  private clearSelectedFreightItems(): void {
    this.selectedProducts = [];
    this.manualPackages = [];
    this.manualPackageForm = this.createManualPackageForm();
  }

  private applyFreightPresentation(presentation: CustomerFreightPresentation | null | undefined): void {
    this.freightPresentation = presentation ?? null;

    const preferredRateMethod = presentation?.preferred_rate_method ?? null;
    if (preferredRateMethod !== null) {
      this.quoteForm.freight.preferred_rate_method = preferredRateMethod;
    }

    if (!this.showPackageBuilder) {
      this.clearSelectedFreightItems();
    } else {
      if (!this.allowCatalogProducts) {
        this.selectedProducts = [];
      }
      if (!this.allowManualPackages) {
        this.manualPackages = [];
        this.manualPackageForm = this.createManualPackageForm();
      }
    }

    const numericFields: Array<Exclude<FreightFieldKey, 'load_type'>> = [
      'total_weight_lb',
      'linear_feet',
      'handling_units',
      'cubic_feet',
      'density_lb_per_cuft',
      'delivery_type'
    ];
    numericFields.forEach((field) => {
      if (!this.isFreightFieldVisible(field)) {
        this.quoteForm.freight[field] = null;
      }
    });
    if (!this.isFreightFieldVisible('load_type')) {
      this.quoteForm.freight.load_type = '';
    }

    this.response = null;
    this.clearRoutePreview();
    this.resetQuoteSubmissionState();
  }

  private resetFreightForCustomer(): void {
    this.quoteForm.freight = this.createDefaultForm().freight;
    this.freightPresentation = null;
  }

  private clearShippingTargets(): void {
    this.shippingTargetsRequestSequence += 1;
    this.shippingTargets = [];
    this.shippingTargetsLoaded = false;
    this.shippingTargetsLoading = false;
    this.shippingTargetsError = '';
  }

  private clearRoutePreview(): void {
    this.routePreview = null;
    this.routeMapUrl = null;
    this.routePreviewLoading = false;
    this.routePreviewError = '';
  }

  private loadRoutePreview(response: CustomerRateQuoteResponse): void {
    const origin = response.lane?.origin ?? this.buildOriginLocation();
    const destination = response.lane?.destination ?? this.buildLocation(this.quoteForm.destination);

    if (!this.hasLocationValue(origin) || !this.hasLocationValue(destination)) {
      return;
    }

    this.routePreviewLoading = true;
    this.routePreviewError = '';

    this.rateQuoteApi.previewCustomerRoute({ origin, destination }).subscribe({
      next: (routePreview) => {
        this.routePreview = routePreview;
        this.routeMapUrl = routePreview.map_image_data_url
          ? this.sanitizer.bypassSecurityTrustUrl(routePreview.map_image_data_url)
          : null;
        this.routePreviewLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.routePreview = null;
        this.routePreviewLoading = false;
        this.routePreviewError = this.errorText(error, 'Unable to load route preview.');
      }
    });
  }

  private submitQuote(withZoneSelection: boolean): void {
    const validationMessage = this.validateForm(withZoneSelection);
    if (validationMessage) {
      this.errorMessage = validationMessage;
      this.toastr.warning(validationMessage);
      return;
    }

    this.resetQuoteSubmissionState();
    const payload = this.buildPayload();
    if (withZoneSelection) {
      const originZoneUuid = this.currentOriginZoneUuid();
      const destinationZoneUuid = this.currentDestinationZoneUuid();

      if (originZoneUuid) {
        payload.origin_zone_uuid = originZoneUuid;
      }

      if (destinationZoneUuid) {
        payload.destination_zone_uuid = destinationZoneUuid;
      }
    } else {
      this.selectedOriginZoneUuid = '';
      this.selectedDestinationZoneUuid = '';
    }

    this.loading = true;
    this.errorMessage = '';
    this.clearRoutePreview();

    if (payload.freight_items?.length) {
      this.loadRouteBeforeQuote(payload);
      return;
    }

    this.executeQuote(payload, false);
  }

  private loadRouteBeforeQuote(payload: CustomerRateQuoteRequest): void {
    const origin = this.buildOriginLocation();
    const destination = this.buildLocation(this.quoteForm.destination);
    this.routePreviewLoading = true;
    this.routePreviewError = '';

    this.rateQuoteApi.previewCustomerRoute({ origin, destination }).subscribe({
      next: (routePreview) => {
        this.routePreview = routePreview;
        this.routeMapUrl = routePreview.map_image_data_url
          ? this.sanitizer.bypassSecurityTrustUrl(routePreview.map_image_data_url)
          : null;
        this.routePreviewLoading = false;
        if (routePreview.distance_meters !== null && routePreview.distance_meters > 0) {
          payload.freight.route_distance_meters = routePreview.distance_meters;
          payload.freight.route_duration_text = routePreview.duration_text;
          payload.freight.route_static_duration_text = routePreview.static_duration_text;
        }
        this.executeQuote(payload, true);
      },
      error: (error: HttpErrorResponse) => {
        this.routePreview = null;
        this.routePreviewLoading = false;
        this.routePreviewError = this.errorText(error, 'Unable to calculate route mileage.');
        this.executeQuote(payload, false);
      }
    });
  }

  private executeQuote(payload: CustomerRateQuoteRequest, routeAlreadyLoaded: boolean): void {
    this.rateQuoteApi.quoteCustomerRate(payload).subscribe({
      next: (response) => {
        this.response = response;
        this.loading = false;
        this.applyZoneSelectionDefaults(response);
        if (!routeAlreadyLoaded) {
          this.loadRoutePreview(response);
        }

        if (response.status === 'rated') {
          this.lastRatedRequest = this.cloneQuoteRequest(payload);
          this.ratedInputFingerprint = this.quoteRequestFingerprint(this.buildPayload());
          this.toastr.success('Customer rate found.');
          return;
        }

        this.toastr.info(this.statusTitle(response.status));
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.errorMessage = this.errorText(error);
        this.toastr.error(this.errorMessage);
      }
    });
  }

  private validateForm(withZoneSelection: boolean): string {
    const origin = this.buildOriginLocation();
    const destination = this.buildLocation(this.quoteForm.destination);
    const freight = this.buildFreight();

    if (this.canSelectCustomer && !this.selectedCustomer?.value) {
      return 'Select a customer to quote.';
    }

    if (this.productsLoading) {
      return 'Wait for the customer freight profile to finish loading.';
    }

    if (!this.hasLocationValue(origin)) {
      return 'Enter an origin postal code or city.';
    }

    if (!this.hasLocationValue(destination)) {
      return 'Enter a destination postal code or city.';
    }

    if (!this.hasUsableFreightMetric(freight) && this.totalSelectedPackageCount === 0) {
      return 'Select a customer product, add a custom package, or enter a freight measurement.';
    }

    if (withZoneSelection && !this.canRetryWithZones) {
      return 'Select an origin and destination zone.';
    }

    return '';
  }

  private buildPayload(): CustomerRateQuoteRequest {
    const payload: CustomerRateQuoteRequest = {
      origin: this.buildOriginLocation(),
      destination: this.buildLocation(this.quoteForm.destination),
      freight: this.buildFreight()
    };

    const externalReference = this.cleanString(this.quoteForm.external_reference_number);
    if (externalReference) {
      payload.external_reference_number = externalReference;
    }

    const freightItems = this.buildFreightItems();
    if (freightItems.length) {
      payload.freight_items = freightItems;
    }

    if (this.canSelectCustomer && this.selectedCustomer?.value) {
      payload.customer_contact_uuid = this.selectedCustomer.value;
    }

    const quoteDate = this.cleanString(this.quoteForm.quote_date);
    if (quoteDate) {
      payload.quote_date = quoteDate;
    }

    return payload;
  }

  private resetQuoteSubmissionState(): void {
    this.createdQuote = null;
    this.quotePdfError = '';
    this.quoteConditionsAccepted = false;
    this.quoteCreating = false;
    this.quoteCreateError = '';
    this.lastRatedRequest = null;
    this.ratedInputFingerprint = '';
    this.quoteIdempotencyKey = '';
  }

  private cloneQuoteRequest(payload: CustomerRateQuoteRequest): CustomerRateQuoteRequest {
    return JSON.parse(JSON.stringify(payload)) as CustomerRateQuoteRequest;
  }

  private quoteRequestFingerprint(payload: CustomerRateQuoteRequest): string {
    return JSON.stringify(payload);
  }

  private newIdempotencyKey(): string {
    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      crypto.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }

    const random = Array.from(bytes)
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('');
    return `crq-${Date.now()}-${random}`;
  }

  private buildOriginLocation(): RateLocation {
    const manualLocation = this.buildLocation(this.quoteForm.origin);
    if (!this.selectedShippingOrigin) {
      return manualLocation;
    }

    return {
      ...this.selectedShippingOrigin.location,
      ...manualLocation
    };
  }

  private buildLocation(form: RateLocationForm): RateLocation {
    const location: RateLocation = {};
    const postalCode = this.cleanString(form.postal_code);
    const city = this.cleanString(form.city);
    const province = this.cleanString(form.province).toUpperCase();
    const country = this.normalizeCountryCode(form.country);

    if (postalCode) {
      location.postal_code = postalCode.toUpperCase();
    }

    if (city) {
      location.city = city;
    }

    if (province) {
      location.province = province;
    }

    if (country) {
      location.country = country;
    }

    return location;
  }

  private buildFreight(): RateFreight {
    const freight: RateFreight = {};
    if (this.isFreightFieldVisible('total_weight_lb')) {
      this.assignPositiveNumber(freight, 'total_weight_lb', this.quoteForm.freight.total_weight_lb);
    }
    if (this.isFreightFieldVisible('linear_feet') && !this.usesCalculatedLinearFeet) {
      this.assignPositiveNumber(freight, 'linear_feet', this.quoteForm.freight.linear_feet);
    }
    if (this.isFreightFieldVisible('handling_units')) {
      this.assignPositiveNumber(freight, 'handling_units', this.quoteForm.freight.handling_units);
    }
    if (this.isFreightFieldVisible('cubic_feet')) {
      this.assignPositiveNumber(freight, 'cubic_feet', this.quoteForm.freight.cubic_feet);
    }
    if (this.isFreightFieldVisible('density_lb_per_cuft')) {
      this.assignPositiveNumber(freight, 'density_lb_per_cuft', this.quoteForm.freight.density_lb_per_cuft);
    }

    const deliveryType = this.optionalNumber(this.quoteForm.freight.delivery_type);
    if (this.isFreightFieldVisible('delivery_type') && deliveryType !== undefined) {
      freight.delivery_type = deliveryType;
    }

    if (this.quoteForm.freight.preferred_rate_method) {
      freight.preferred_rate_method = this.quoteForm.freight.preferred_rate_method;
    }

    const loadType = this.cleanString(this.quoteForm.freight.load_type);
    if (this.isFreightFieldVisible('load_type') && loadType) {
      freight.load_type = loadType;
    }

    return freight;
  }

  private buildFreightItems(): RateFreightItem[] {
    if (!this.showPackageBuilder) {
      return [];
    }

    const catalogItems: RateFreightItem[] = this.selectedProducts
      .filter(() => this.allowCatalogProducts)
      .filter((selected) => this.positiveWholeNumber(selected.package_quantity) !== undefined)
      .map((selected) => ({
        source: 'catalog',
        customer_product_uuid: selected.product.customer_product_uuid,
        package_quantity: this.positiveWholeNumber(selected.package_quantity) as number,
        floor_rotation_allowed: selected.product.floor_rotation_allowed
      }));

    const manualItems: RateFreightItem[] = this.manualPackages
      .filter(() => this.allowManualPackages)
      .map((selected) => ({
      source: 'manual',
      label: selected.label,
      package_quantity: selected.package_quantity,
      length: selected.length,
      width: selected.width,
      height: selected.height,
      ...(selected.weight !== null ? { weight: selected.weight } : {}),
      measurement_unit: selected.measurement_unit,
      weight_unit: selected.weight_unit,
      stackable: selected.stackable,
      floor_rotation_allowed: selected.floor_rotation_allowed
      }));

    return [...catalogItems, ...manualItems];
  }

  private applyOriginLocation(location: RateLocation): void {
    this.quoteForm.origin = {
      postal_code: this.cleanString(location.postal_code || location.zip || location.postal).toUpperCase(),
      city: this.cleanString(location.city || location.locality),
      province: this.cleanString(location.province || location.state || location.region).toUpperCase(),
      country: this.normalizeCountryCode(location.country || this.quoteForm.origin.country || 'CA')
    };
  }

  private clearOriginForm(): void {
    this.quoteForm.origin = {
      postal_code: '',
      city: '',
      province: '',
      country: 'CA'
    };
  }

  private shippingOriginOptionsForTarget(target: CustomerShippingTarget, targetIndex: number): ShippingOriginOption[] {
    const addresses = target.addresses?.length ? target.addresses : [];
    if (!addresses.length) {
      return [this.shippingOriginOption(target, null, target.origin_location, targetIndex, 0)];
    }

    return addresses.map((address, addressIndex) => this.shippingOriginOption(
      target,
      address,
      this.locationFromShippingAddress(target, address),
      targetIndex,
      addressIndex
    ));
  }

  private shippingOriginOption(
    target: CustomerShippingTarget,
    address: CustomerShippingTargetAddress | null,
    location: RateLocation,
    targetIndex: number,
    addressIndex: number
  ): ShippingOriginOption {
    const addressUuid = address?.contact_address_uuid || location.contact_address_uuid || 'default';
    return {
      id: (target.relationship_uuid || target.target_contact_uuid || String(targetIndex)) + ':' + addressUuid + ':' + addressIndex,
      label: target.display_name || target.target.display_name || 'Shipping origin',
      subline: this.shippingOriginSubline(address, location),
      location,
      target,
      address
    };
  }

  private locationFromShippingAddress(target: CustomerShippingTarget, address: CustomerShippingTargetAddress): RateLocation {
    return {
      contact_uuid: address.contact_uuid || target.target_contact_uuid || undefined,
      contact_address_uuid: address.contact_address_uuid || undefined,
      postal_code: address.address_postal_code || undefined,
      locality: address.address_locality || undefined,
      city: address.address_locality || undefined,
      region: address.address_region || undefined,
      province: address.address_region || undefined,
      country: address.address_country || undefined,
      lat: address.lat ?? undefined,
      lng: address.lng ?? undefined
    };
  }

  private shippingOriginSubline(address: CustomerShippingTargetAddress | null, location: RateLocation): string {
    const label = this.cleanString(address?.address_label || address?.address_type);
    const street = this.cleanString(address?.address_street);
    const city = this.cleanString(address?.address_locality || location.city || location.locality);
    const region = this.cleanString(address?.address_region || location.province || location.region || location.state);
    const postal = this.cleanString(address?.address_postal_code || location.postal_code || location.zip || location.postal);
    const country = this.cleanString(address?.address_country || location.country);
    const locality = [city, region, postal, country].filter((part) => part !== '').join(', ');
    return [label, street, locality].filter((part) => part !== '').join(' / ') || 'No address on file';
  }

  private assignPositiveNumber<T extends keyof RateFreight>(target: RateFreight, key: T, value: number | null): void {
    const parsed = this.positiveNumber(value);
    if (parsed !== undefined) {
      target[key] = parsed as never;
    }
  }

  private positiveNumber(value: number | null | undefined): number | undefined {
    const parsed = this.optionalNumber(value);
    if (parsed === undefined || parsed <= 0) {
      return undefined;
    }

    return parsed;
  }

  private optionalNumber(value: number | null | undefined): number | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return undefined;
    }

    return parsed;
  }

  private positiveWholeNumber(value: number | null | undefined): number | undefined {
    const parsed = this.positiveNumber(value);
    if (parsed === undefined || !Number.isInteger(parsed)) {
      return undefined;
    }

    return parsed;
  }

  private hasUsableFreightMetric(freight: RateFreight): boolean {
    return this.isPositive(freight.total_weight_lb)
      || this.isPositive(freight.weight_lb)
      || this.isPositive(freight.linear_feet)
      || this.isPositive(freight.density_lb_per_cuft)
      || (this.isPositive(freight.cubic_feet) && this.isPositive(freight.total_weight_lb));
  }

  private hasLocationValue(location: RateLocation): boolean {
    return Boolean(location.postal_code || location.city || (location.lat !== undefined && location.lng !== undefined));
  }

  private isPositive(value: number | null | undefined): boolean {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
  }

  private currentOriginZoneUuid(): string {
    return this.selectedOriginZoneUuid || this.response?.lane.origin_zone?.zone_uuid || '';
  }

  private currentDestinationZoneUuid(): string {
    return this.selectedDestinationZoneUuid || this.response?.lane.destination_zone?.zone_uuid || '';
  }

  private applyZoneSelectionDefaults(response: CustomerRateQuoteResponse): void {
    if (response.status !== 'needs_zone_selection') {
      return;
    }

    this.selectedOriginZoneUuid = this.defaultZoneUuid(response.lane.origin_zone);
    this.selectedDestinationZoneUuid = this.defaultZoneUuid(response.lane.destination_zone);
  }

  private defaultZoneUuid(resolution: ZoneResolution | null): string {
    if (resolution?.zone_uuid) {
      return resolution.zone_uuid;
    }

    const matches = resolution?.matches ?? [];
    return matches.length === 1 ? matches[0].zone_uuid : '';
  }

  private toneForStatus(status: CustomerRateQuoteStatus | null | undefined): StatusTone {
    switch (status) {
      case 'rated':
        return 'success';
      case 'needs_zone_selection':
      case 'needs_location_confirmation':
      case 'no_rate_found':
        return 'warning';
      case 'location_not_found':
      case 'zone_not_found':
        return 'danger';
      case undefined:
      case null:
        return 'idle';
      default:
        return 'info';
    }
  }

  private createDefaultForm(): RateQuoteForm {
    return {
      origin: {
        postal_code: '',
        city: '',
        province: '',
        country: 'CA'
      },
      destination: {
        postal_code: '',
        city: '',
        province: '',
        country: 'CA'
      },
      external_reference_number: '',
      quote_date: this.todayString(),
      freight: {
        total_weight_lb: null,
        linear_feet: null,
        handling_units: null,
        cubic_feet: null,
        density_lb_per_cuft: null,
        delivery_type: null,
        preferred_rate_method: null,
        load_type: ''
      }
    };
  }

  private createManualPackageForm(): ManualPackageDraft {
    return {
      label: '',
      package_quantity: 1,
      length: null,
      width: null,
      height: null,
      weight: null,
      measurement_unit: 'IN',
      weight_unit: 'LB',
      stackable: false,
      floor_rotation_allowed: true
    };
  }

  private effectiveSelectedCustomerUuid(): string | undefined {
    return this.canSelectCustomer ? this.selectedCustomer?.value : undefined;
  }

  private productSearchText(product: CustomerRateProduct): string {
    return [
      product.product_model_number,
      product.product_name,
      product.product_description,
      product.product_barcode,
      product.product_class
    ].filter(Boolean).join(' ').toLowerCase();
  }

  private todayString(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private cleanString(value: string | null | undefined): string {
    return String(value ?? '').trim();
  }

  private normalizeCountryCode(value: string | null | undefined): string {
    const country = this.cleanString(value).toUpperCase();
    if (['CA', 'CAN', 'CANADA'].includes(country)) {
      return 'CA';
    }

    if (['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(country)) {
      return 'US';
    }

    return country;
  }

  private formatConfidence(confidence: number | null | undefined): string {
    if (confidence === null || confidence === undefined || !Number.isFinite(confidence)) {
      return '';
    }

    const normalized = confidence <= 1 ? confidence * 100 : confidence;
    return `${Math.round(normalized)}% match`;
  }

  private errorText(error: HttpErrorResponse, fallback = 'Unable to calculate customer rate.'): string {
    const payload = error.error as { message?: string; error?: string } | string | null;
    if (typeof payload === 'string' && payload.trim()) {
      return payload;
    }

    if (payload && typeof payload === 'object') {
      return payload.message || payload.error || fallback;
    }

    return error.message || fallback;
  }
}
